import { Box } from "@mui/material"
import {
  FocusContext,
  getCurrentFocusKey,
  useFocusable,
  doesFocusableExist,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation"
import clsx from "clsx"
import _ from "lodash"
import React, {
  memo,
  MutableRefObject,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { NavigateFunction, useLocation } from "react-router"
import useSdk, { CuratedContentPageQuery, ErrorLevel } from "src/hooks/use-sdk"

import { Banner } from "../../components/Banner/Banner"
import { FilteredBannerItem } from "../../components/Banner/use-banner"
import { Footer } from "../../components/common/Footer"
import { PaginationButton } from "../../components/common/PaginationButton"
import { ProgressSpinner } from "../../components/ProgressSpinner"
import { Slider, Sliders, TTeaser } from "../../components/Sliders/Slider"
import { useRefreshControl } from "../../hooks/use-refresh-control"
import { useRootPageTracking } from "../../hooks/use-root-page-tracking"
import { SliderExtract, useSliders } from "../../hooks/use-sliders"
import { useSmoothNavigate } from "../../hooks/use-smooth-navigate"
import { appStore } from "../../store/AppStore"
import { focusStore } from "../../store/FocusStore"
import { playerStore } from "../../store/PlayerStore"
import {
  capitalizeFirstLetter,
  CuratedContentSlugV2,
  exitApp,
  reReadAriaLabel,
  s,
} from "../../utilities/common"
import { dimensions, maxSliderSize } from "../../utilities/constants"
import { isContentAvailable } from "../../utilities/content-availability-helpers"
import {
  AddFocusFunctionProps,
  useFocusHelper,
} from "../../utilities/use-focus-helper"
import { useRouteMatch } from "../../utilities/use-route-match"

import { TabviewProps } from "./Tabview"

export const CuratedScreen = ({ slug }: TabviewProps) => {
  const sdk = useSdk(ErrorLevel.WithPrompt, () => exitApp())
  const setCuratedContent = appStore((state) => state.setCuratedContent)
  const [data, setData] = useState<CuratedContentPageQuery>()
  const finalData = useDeferredValue(data)
  const accessToken = appStore((state) => state.accessTokenState)
  const setSpinner = appStore((state) => state.setSpinner)

  const finalSlug = useDeferredValue(slug)
  const storeSlug = appStore((state) => state.slug)
  const nextFocusIsReset = useRef(false)

  useEffect(() => {
    if (storeSlug === slug) {
      load()
      setSpinner("out")
    }
  }, [storeSlug])

  const load = useCallback(
    async (force = false) => {
      if (!force && data) return
      // We limit this function to the DISCOVER page, so it doesn't
      // get called multiple times (although it doesn't break anything if it was)
      if (slug === CuratedContentSlugV2.Discover) {
        document.getElementById("root")!.style.transform =
          "translate3d(0, 0, 0)"
      }
      //
      /*
      if the page is reset, make sure that the next getFocus call (which means
      the user RETURNS to a main screen) focuses the Banner, because the page
      is automatically scrolled to the top.
       */
      if (force) nextFocusIsReset.current = true
      setTimeout(async () => {
        const result = await sdk.curatedContentPage({
          slug,
          sliderPageSize:
            slug === CuratedContentSlugV2.Live ? 999 : maxSliderSize,
        })
        setData(result)
        setCuratedContent(slug, result)
      }, 300)
    },
    [slug, data],
  )

  useRefreshControl(() => load(true))

  useEffect(() => {
    if (finalData) load(true)
  }, [accessToken])

  if (!finalData) {
    return <ProgressSpinner />
  }
  return (
    <Screen
      finalData={finalData}
      slug={finalSlug}
      nextFocusIsReset={nextFocusIsReset}
    />
  )
}

const Screen = memo(
  ({
    finalData,
    slug,
    nextFocusIsReset,
  }: {
    finalData: CuratedContentPageQuery
    slug: CuratedContentSlugV2
    nextFocusIsReset: MutableRefObject<boolean>
  }) => {
    const isMyLocation = useRouteMatch()
    const setSpinner = appStore((state) => state.setSpinner)
    const geoInfo = appStore((state) => state.geoInfo)
    const storeSlug = appStore((state) => state.slug)
    const prompt = appStore((state) => state.prompt)
    const [visible, setVisible] = useState(false)
    const [banners, setBanners] =
      useState<CuratedContentPageQuery["curatedContentPage"]["stageSlider"]>()
    const navigationProps = playerStore((state) => state.navigationProps)

    useRootPageTracking(visible, capitalizeFirstLetter(slug))

    useEffect(() => {
      setVisible(slug === storeSlug)
    }, [storeSlug])
    useLayoutEffect(() => {
      if (finalData && banners && sliders) {
        const currentFocusKey = getCurrentFocusKey()
        if (
          isMyLocation &&
          visible &&
          currentFocusKey &&
          // don't steal focus after load, if already focusing elements that are
          // outside the API-loaded zone and on the same page (currently only these two)
          !currentFocusKey.startsWith("TABBAR") &&
          !currentFocusKey.startsWith("SEARCHSCREEN") &&
          !navigationProps.open
        ) {
          const resolvedKey = getFocus({ noScroll: true })
          if (
            nextFocusIsReset.current ||
            !resolvedKey ||
            !doesFocusableExist(resolvedKey)
          ) {
            nextFocusIsReset.current = false
            if (
              // Search overlays also don't have their own route, but in order to alleviate the use
              // of states, we will only check if these modals exist
              !document.getElementById("SearchPrmpt") &&
              !document.getElementById("FacetBrowse") &&
              !prompt
            )
              setFocus("TABBAR")
          }
          setTimeout(() => setSpinner("out"), 200) // DGAPP-5304 (avoid flashing main screen before the search modal)
        }
      }
    }, [isMyLocation, visible, finalData, banners, navigationProps])

    const sliders = useSliders(
      finalData.curatedContentPage.curatedContent as SliderExtract[],
      slug,
    )

    const { getFocus, addFocus: addFocusInit } = useFocusHelper()

    const addFocus = (props?: AddFocusFunctionProps) => {
      addFocusInit(props)
      nextFocusIsReset.current = false
    }

    useEffect(() => {
      if (finalData) {
        const bannerData = finalData.curatedContentPage.stageSlider
        setBanners(
          bannerData
            .filter(
              (banner) =>
                banner.item.__typename === "Album" ||
                banner.item.__typename === "Artist" ||
                banner.item.__typename === "Group" ||
                banner.item.__typename === "VodConcert" ||
                banner.item.__typename === "LiveConcert" ||
                banner.item.__typename === "Video" ||
                banner.item.__typename === "Partner" ||
                banner.item.__typename === "EditorialPlaylist",
            )
            .filter((item) =>
              isContentAvailable(item.item as FilteredBannerItem, geoInfo),
            ),
        )
      }
    }, [finalData])
    const navigate = useSmoothNavigate()

    if (!(finalData && banners && sliders)) return null
    return (
      <FocusArea
        visible={visible}
        banners={banners}
        slug={slug}
        sliders={sliders}
        navigate={navigate}
        addFocus={addFocus}
      />
    )
  },
)

// Isolate Focused component to prevent warnings
const FocusArea = ({
  visible,
  banners,
  sliders,
  addFocus,
  slug,
  navigate,
}: Props & { visible: boolean }) => {
  const setMinScreenHeight = appStore((state) => state.setMinScreenHeight)
  const isMyRoute = useRouteMatch()
  // this ensures that the home screen is tall enough to display the entire content
  useLayoutEffect(() => {
    setTimeout(() => {
      const height = ref.current?.offsetHeight
      if (height > 0) setMinScreenHeight(height)
    }, 1000)
  }, [])
  const { ref, focusKey } = useFocusable({
    focusable: visible && isMyRoute,
    saveLastFocusedChild: false,
  })
  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          overflowY: "visible",
          width: dimensions.width,
        }}
      >
        <MemoizedScreen
          banners={banners}
          sliders={sliders}
          addFocus={addFocus}
          slug={slug}
          navigate={navigate}
        />
      </Box>
    </FocusContext.Provider>
  )
}

interface Props {
  banners: CuratedContentPageQuery["curatedContentPage"]["stageSlider"]
  sliders: Sliders
  addFocus: () => void
  slug: string
  navigate: NavigateFunction
}
const MemoizedScreen = memo(
  ({ banners, sliders: preSliders, addFocus, slug, navigate }: Props) => {
    const [sliders, setSliders] = useState<Sliders>()
    // render sliders with delay to improve initial load of curated screen
    useEffect(() => {
      setTimeout(() => setSliders(preSliders), 5)
    }, [preSliders])

    return (
      <>
        <FocusableBanners banners={banners} slug={slug} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "min-content",
            pb: s("2px"),
            pt: s("2px"),
          }}
        >
          {sliders
            ? sliders.map((slider, sliderIndex) => (
                <Slider
                  key={slider.title + slider.id}
                  onPressBrowse={() => {
                    addFocus()
                    navigate(`browse/0/${slider.id}/1/0`)
                  }}
                  onPressTitle={(d?: TTeaser, teaserIndex?: number) => {
                    if (!d || teaserIndex === undefined) return
                    addFocus()
                    if (d.type !== "CuratedContentLink")
                      navigate(
                        `detail/${d.type}/${d.id}/${sliderIndex + 1}/${
                          teaserIndex + 1
                        }/${slider.id}`,
                      )
                    else navigate(`category/${d.slugV2}`)
                  }}
                  onFocus={({ top: y, height }, _, query) => {
                    if (!(query && query.noScroll)) {
                      document.getElementById("root")!.style.transform =
                        "translate3d(0, " +
                        Math.min(0, -y + (dimensions.height - height) / 2) +
                        "px, 0)"
                    }
                  }}
                  title={slider.title}
                  prefix={slug}
                  data={slider}
                  dimensions={slider.style}
                  margin={sliderIndex < sliders?.length - 1}
                />
              ))
            : null}
        </Box>
        {sliders ? <Footer /> : null}
      </>
    )
  },
  (prevProps, nextProps) => {
    // memoize on everything except navigation stuff
    return (
      _.isEqual(prevProps.sliders, nextProps.sliders) &&
      _.isEqual(prevProps.banners, nextProps.banners)
    )
  },
)
const FocusableBanners = ({
  slug,
  banners: bannersUnfiltered,
}: {
  slug: string
  banners: CuratedContentPageQuery["curatedContentPage"]["stageSlider"]
}) => {
  // FIXME add playlists once available
  const banners = bannersUnfiltered.filter(
    (banner) =>
      banner.item.__typename === "Album" ||
      banner.item.__typename === "Artist" ||
      banner.item.__typename === "Group" ||
      banner.item.__typename === "VodConcert" ||
      banner.item.__typename === "LiveConcert" ||
      banner.item.__typename === "Video" ||
      banner.item.__typename === "Partner" ||
      banner.item.__typename === "EditorialPlaylist",
  )
  const [bannerIndex, setBannerIndex] = useState(0)
  const addFocus = focusStore((state) => state.addFocus)
  const navigate = useSmoothNavigate()
  const location = useLocation()
  const slideBanner = useCallback(
    (direction: "left" | "right") => {
      if (direction === "left") {
        if (bannerIndex - 1 < 0) setBannerIndex(banners.length - 1)
        else setBannerIndex(bannerIndex - 1)
        return false
      }
      if (direction === "right") {
        if (bannerIndex + 1 >= banners.length) setBannerIndex(0)
        else setBannerIndex(bannerIndex + 1)
        return false
      }
    },
    [bannerIndex],
  )

  const onPress = useCallback(() => {
    addFocus({ route: location.pathname + location.key, key: slug + "BANNER" })
    navigate(
      `detail/${banners[bannerIndex].item.__typename}/${banners[bannerIndex].item.id}/0/0/0`,
    )
  }, [navigate, banners, bannerIndex])

  const { ref, focused, focusSelf } = useFocusable({
    focusable: banners.length > 0,
    focusKey: slug + "BANNER",
    isFocusBoundary: true,
    focusBoundaryDirections: ["left", "right"],
    onFocus: (_, __, query) => {
      if (!(query && query.noScroll))
        document.getElementById("root")!.style.transform =
          "translate3d(0, 0, 0)"
    },
    onEnterPress: onPress,
    onArrowPress: (direction) => {
      if (direction === "left" || direction === "right") {
        slideBanner(direction)
        reReadAriaLabel(ref)
        return false
      }
      return true
    },
  })

  return (
    <>
      <Box
        ref={ref}
        onMouseEnter={() => focusSelf({ noScroll: true })}
        onMouseDown={onPress}
        data-testid={"banner" + slug}
        tabIndex={0}
        aria-label={
          (banners[bannerIndex]?.title ?? "") +
          " " +
          (banners[bannerIndex]?.subtitle ?? "")
        }
      >
        {banners.length ? (
          <Box
            sx={{
              transform: focused ? "scale(1.05)" : undefined,
              width: s("1728px"),
              height: s("558px"),
            }}
          >
            {banners.map((banner, i) => (
              <Box
                key={slug + "BANNER" + i}
                className={clsx(focused && "focused")}
                sx={{
                  position: "absolute",
                  transition: "opacity 0.2s linear",
                  opacity: i === bannerIndex ? "1" : "0",
                }}
              >
                <Banner data={banner} />
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
      {banners.length ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            mt: s("57px"),
            mb: s("70.5px"),
            justifyContent: "center",
          }}
        >
          {[...Array(banners.length)].map((_, i) => (
            <PaginationButton
              onMouseDown={() => setBannerIndex(i)}
              key={"paginationButtons" + i}
              index={i}
              selected={bannerIndex === i}
            />
          ))}
        </Box>
      ) : null}
    </>
  )
}
